import { useState } from 'react'
import styled from 'styled-components'
import { CustomProvider, DateRangePicker } from 'rsuite'
const { afterToday } = DateRangePicker
import ptBR from 'rsuite/locales/pt_BR'
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import {
  AutoCompleteWrapper,
  ChartWithNoteWrapper,
  CustomTableCell,
  Filter,
  FilterDrawer,
  GroupedFilterTags,
  TableHeadWithoutOrdenation,
} from '../../components'
import { padToTwoDigits } from '../../utils/dateConversion'
import {
  PredictionHistoryProvider,
  usePredictionHistory,
} from '../../contexts/PredictionHistoryContext'
import useViewport from '../../hooks/useViewport'
import filterDrawerHelper from '../../utils/filterDrawer'
import { RequestService } from '../../services/RequestService'
import { useTranslation } from "react-i18next"

const Container = styled(Box)`
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
`

const Body = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #f1f3f5;
  padding: 24px 44px;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ChartContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 8px;
  padding: 16px;
  margin-top: 24px;
`

const FiltersTagsContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const FiltersContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`

const generateHourCells = ({ hours }) => {
  let elements = []

  for (let index = 0; index <= 23; index++) {
    elements.push(
      <CustomTableCell
        type='value'
        data={Math.round(hours[`${padToTwoDigits(index)}`])}
        align='center'
      />,
    )
  }

  return elements
}

const PredictionHistoryWrapper = () => {
  const [openFilterDrawer, setOpenFilterDrawer] = useState(false)
  const {
    rows,
    loading,
    dateRange,
    onDateRangeChange,
    filterOptions,
    loadingFilters,
    onSelectFilter,
    selectedFilterOptions,
    selectedUser,
    onSelectUser,
    onShift,
    onCheckOnShift,
    inputValue,
    onInputValueChange,
    graphMessage,
  } = usePredictionHistory()
  const { width } = useViewport()
  const { getMaxTagsToShow, selectedFilterOptionsList } = filterDrawerHelper
  const { t } = useTranslation('common')

  const generateInitialHeadState = () => {
    let elements = []

    for (let index = 0; index <= 23; index++) {
      elements.push({
        id: `${padToTwoDigits(index)}`,
        numeric: false,
        disablePadding: true,
        label: `${padToTwoDigits(index)}h`,
        align: 'center',
      })
    }

    return [
      {
        id: `day`,
        numeric: false,
        disablePadding: true,
        label: `Dia`,
        align: 'center',
      },
      ...elements,
    ]
  }

  const initialHeadState = generateInitialHeadState()
  const headCells = initialHeadState

  const emptyRows = () => {
    return rows.length < 10 ? Math.max(0, rows.length) : 0
  }

  return (
    <Container>
      <Body>
        <Title>
          <span style={{ fontSize: 20, fontWeight: 600 }}>
            {t('prediction_history.header')}
          </span>
          <Tooltip
            title={t('prediction_history.description')}
            placement='right'
            arrow
          >
            <HelpOutline fontSize='18' />
          </Tooltip>
        </Title>
        <ChartContainer>
          <FiltersTagsContainer>
            <FiltersContainer>
              <div style={{ display: 'flex', flex: 1.5 }}>
                <AutoCompleteWrapper
                  onLoad={() => RequestService.getUsers({ visibility: 'reports' })}
                  value={selectedUser}
                  onChangeValue={onSelectUser}
                  inputValue={inputValue}
                  onInputValueChange={onInputValueChange}
                />
              </div>
              <div>
                <div
                  style={{
                    display: 'flex',
                    flex: 1,
                    justifyContent: 'flex-end',
                    gap: 16,
                  }}
                >
                  <CustomProvider locale={ptBR}>
                    <DateRangePicker
                      defaultCalendarValue={dateRange}
                      value={dateRange}
                      onChange={onDateRangeChange}
                      showOneCalendar
                      shouldDisableDate={afterToday()}
                      character=' - '
                      placement='bottom'
                      placeholder={t('prediction_history.placeholder')}
                      ranges={[]}
                      cleanable={false}
                    />
                  </CustomProvider>
                  <Filter onClick={() => setOpenFilterDrawer(true)} />
                </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={onShift === 'Sim'}
                      onChange={() =>
                        onCheckOnShift(onShift === 'Sim' ? 'Não' : 'Sim')
                      }
                    />
                  }
                  label={t('prediction_history.label')}
                />
              </div>
            </FiltersContainer>
            {selectedFilterOptionsList({
              selectedFilterOptions,
            })?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <GroupedFilterTags
                  tags={selectedFilterOptionsList({
                    selectedFilterOptions,
                  })}
                  onTagClick={() => setOpenFilterDrawer(prev => !prev)}
                  maxTagsToShow={getMaxTagsToShow({ width })}
                />
              </div>
            )}
          </FiltersTagsContainer>
          <ChartWithNoteWrapper
            totalHours={graphMessage?.totalHours}
            totalUsers={graphMessage?.totalUsers}
          >
            <TableContainer sx={{ maxHeight: 650 }}>
              <Table
                size='small'
                aria-labelledby='tableTitle'
                stickyHeader
              >
                <TableHeadWithoutOrdenation headCells={headCells} />

                <TableBody>
                  {!loading ? (
                    <>
                      {rows.length > 0 ? (
                        <>
                          {rows.map(row => {
                            return (
                              <TableRow
                                hover
                                tabIndex={-1}
                                key={row.id}
                              >
                                <CustomTableCell
                                  data={row.day}
                                  sx={{ minWidth: 80 }}
                                />
                                {generateHourCells({ hours: row.hour }).map(
                                  column => column,
                                )}
                              </TableRow>
                            )
                          })}
                          {emptyRows() > 0 && (
                            <TableRow
                              style={{
                                height: 53 * emptyRows(),
                              }}
                            >
                              <TableCell colSpan={25} />
                            </TableRow>
                          )}
                        </>
                      ) : (
                        <TableRow style={{ height: 53 * 10 }}>
                          <TableCell
                            colSpan={25}
                            align='center'
                          >
                            {t('no_data')}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ) : (
                    <TableRow style={{ height: 53 * 10 }}>
                      <TableCell
                        colSpan={loading ? 25 : 100}
                        align='center'
                      >
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartWithNoteWrapper>
        </ChartContainer>
      </Body>

      <FilterDrawer
        open={openFilterDrawer}
        onClose={() => {
          setOpenFilterDrawer(false)
        }}
        onSave={onSelectFilter}
        loading={loadingFilters}
        options={filterOptions}
        withShift
        withPosition
        withTeam
      />
    </Container>
  )
}

const PredictionHistory = () => {
  return (
    <PredictionHistoryProvider>
      <PredictionHistoryWrapper />
    </PredictionHistoryProvider>
  )
}

export default PredictionHistory
